import React, { useState, useEffect } from 'react';
import {
  CreateContainer,
  CreateFlex,
  CreateTitle,
  CreateAlertContainer,
  CreateListContainer,
  CreatePermsContainer,
  CreateAccessContainer,
  CreateButtonContainer,
  CreateButton,
} from './styles';
import { TextField, FormControl, FormGroup, FormControlLabel, Checkbox, Icon, IconButton } from '@mui/material';
import { UserPermission } from 'global/lib/filter';
import { get, set, size } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import DropRealms from 'global/components/droprealms';
import { _size } from 'global/utils';
import Alert from '@mui/material/Alert';
import { findServerPerm, validateEmail } from 'global/utils';
import useDebounce from 'global/hooks/useDebounce';
import sendData from 'redux/actions/sendData';
import { CenterLoader, Loader } from 'global/styles';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';
import Modal from 'global/components/modal';

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const [perms, setPerms] = useState({});
  const [ugs, setUgs] = useState({});
  const [selectedRealms, setSelectedRealms] = useState({});
  const [emailText, setEmailText] = useState('');
  const [alerts, setAlerts] = useState();
  const [emailError, setEmailError] = useState();
  const [sending, setSending] = useState(false);
  const debouncedEmail = useDebounce(emailText, 1000);
  const { me, siteContextSelectedRealm: hasRealm } = useUser();
  const { filterPermissionsByNav } = useNavSettings();
  const [modal, setModal] = useState({ open: false });

  const permData = useRetrieveData({ url: `/permissions` }, { build: ['permissions'] });
  const userGroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'] });

  const handlePermissionChange = (name) => (e) => {
    const p = { ...perms };
    set(p, name, e.target.checked);
    setPerms(p);
  };

  const handleUgChange = (name) => (e) => {
    const p = { ...ugs };
    set(p, name, e.target.checked);
    setUgs(p);
  };

  const onUpdateRealms = (realms) => {
    setSelectedRealms(realms);
  };

  const clear = () => {
    setEmailText('');
    setSelectedRealms({});
    setEmailError();
    setPerms({});
    setSending(false);
  };

  const testEmails = () => {
    const emails = emailText
      .split('\n')
      .map((e) => e.trim())
      .filter((e) => e);
    const valid = emails.filter((e) => validateEmail(e));

    return {
      valid: size(emails) === size(valid),
      emails,
    };
  };

  const createUsers = async () => {
    const { emails } = testEmails();

    const parentRealm = (() => {
      let lowest;
      Object.keys(selectedRealms).forEach((k) => {
        const realm = selectedRealms[k];
        if (!lowest || lowest > realm.parentId) {
          lowest = realm.parentId || realm.id;
        }
      });
      return lowest;
    })();

    const resps = await Promise.all(
      emails.map(async (e) => {
        const hRealms = _size(selectedRealms) > 0;
        const hPerms = _size(perms) > 0;
        const hUgs = _size(ugs) > 0;
        return await sendData({
          url: `/users?include=realm,realms,permissions,userGroups`,
          method: 'post',
          data: {
            data: {
              type: 'users',
              attributes: {
                realm_id: get(me, 'realm.id'),
                name: e,
                email: e,
              },
              relationships: {
                realms: hRealms
                  ? {
                      data: Object.keys(selectedRealms)
                        // .filter((k) => selectedRealms[k].id !== parentRealm)
                        .map((k) => ({ type: 'realms', id: get(selectedRealms, [k, 'id']) })),
                    }
                  : undefined,
                permissions: hPerms
                  ? {
                      data: Object.keys(perms)
                        .filter((k) => perms[k])
                        .map((k) => {
                          const name = `${get(
                            UserPermission.find((up) => up.value === k),
                            'match'
                          )}.${parentRealm || '*'}`;
                          const permId = findServerPerm(permData.data, name);
                          return !permId
                            ? null
                            : {
                                type: 'permissions',
                                id: get(permId, 'id', '').toString(),
                              };
                        })
                        .filter((f) => f),
                    }
                  : undefined,
                userGroups: hUgs ? { data: Object.keys(ugs).filter((k) => ugs[k]).map((k) => ({ type: 'user-groups', id: `${k}` })) } : undefined,
              },
            },
          },
        });
      })
    );

    const setEmailAlert = (count) => {
      setAlerts({
        message: `Registration emails have been sent to ${count} user${count === 1 ? '' : 's'}`,
        severity: 'success',
      });
    };

    const updated = resps.filter((usr) => get(usr, 'data.data.attributes.updated') === 1);

    if (size(updated)) {
      const content = (() => {
        const names = updated.map((up) => get(up, 'data.data.attributes.email'));
        const joined = names.map((n, i) => {
          if (i === 0) {
            return n;
          } else if (i === names.length - 1) {
            return ` and ${n}`;
          } else {
            return `, ${n}`;
          }
        });
        return (
          <>
            <strong>{joined.join('')}</strong>
            {` already ${names.length > 1 ? 'have' : 'has'} an account on PERFORM. Would you like to reset their password and email them?`}
          </>
        );
      })();

      const close = () => {
        setModal({ open: false });
      };

      const setAlertsOnClose = () => {
        close();
        setEmailAlert(size(emails) - size(updated));
      };

      const notifyUsers = async () => {
        try {
          setModal((m) => ({ ...m, content: <CenterLoader />, buttons: [] }));
          await Promise.all(
            updated.map(async (usr) => {
              const attributes = get(usr, 'data.data.attributes', {});

              return await sendData({
                url: `/users`,
                method: 'post',
                data: {
                  data: {
                    type: 'users',
                    attributes: {
                      name: get(attributes, 'name'),
                      email: get(attributes, 'email'),
                      realm_id: get(usr, 'data.data.relationships.realm.data.id'),
                      self_register_email: true,
                    },
                  },
                },
              });
            })
          );
          setEmailAlert(size(emails));
        } catch (err) {
          setAlerts({
            message: err.message,
          });
        } finally {
          close();
        }
      };

      setModal({
        open: true,
        title: 'Notify Users',
        content,
        buttons: [
          { title: 'Yes', onClick: notifyUsers },
          { title: 'No', onClick: setAlertsOnClose },
        ],
        onClose: close,
      });
    } else {
      setEmailAlert(size(emails));
    }
  };

  useEffect(() => {
    const { valid } = testEmails();
    if (!valid) {
      setEmailError('Invalid email, please fix');
    }
  }, [debouncedEmail]);

  const submit = async () => {
    const { valid, emails } = testEmails();
    if (!valid) {
      return setAlerts({
        message: 'Invalid email, please fix',
      });
    } else {
      setAlerts();
    }

    try {
      setSending(true);
      await createUsers();
      clear();
    } catch (err) {
      setSending(false);
      setAlerts({
        message: `Error: ${err.message}`,
      });
    }
  };

  const changeEmailText = (e) => {
    setEmailText(e.target.value);
    setEmailError();
  };

  const canSubmit = size(emailText) && !emailError && _size(selectedRealms) && !sending;

  const hasUserBuilderRealm = get(me, 'realms', []).find((r) => r.tier === 'builder');

  const canClear = size(emailText) || _size(selectedRealms) || _size(perms);

  return (
    <CreateContainer>
      {!!alerts && (
        <CreateAlertContainer>
          <Alert
            severity="error"
            {...alerts}
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setAlerts()}>
                <Icon>close</Icon>
              </IconButton>
            }
          >
            {get(alerts, 'message')}
          </Alert>
        </CreateAlertContainer>
      )}
      <CreateFlex>
        <CreateListContainer>
          <CreateTitle>{'LIST OF EMAIL ADDRESSES'}</CreateTitle>
          <TextField data-testid='idListEmailAddresses' error={!!emailError} helperText={emailError} value={emailText} onChange={changeEmailText} multiline rows={10} fullWidth={true} variant="outlined" />

          <CreateAccessContainer>
            <CreateTitle>{'ACCESS'}</CreateTitle>
            <DropRealms
              showModify={true}
              currentRealm={hasRealm}
              canOnlySelectUsersBuilder
              realms={Object.keys(selectedRealms).map((k) => selectedRealms[k])}
              canBackOutOfBuilder={hasUserBuilderRealm}
              onUpdate={onUpdateRealms}
              onlyTiers={['builder', 'division']}
            />
          </CreateAccessContainer>
        </CreateListContainer>

        <CreatePermsContainer>
          <div>
            <CreateTitle>{'PERMISSIONS'}</CreateTitle>
            <FormControl component="fieldset">
              <FormGroup>
                {filterPermissionsByNav().map((ed, i) => (
                  <FormControlLabel key={i} control={<Checkbox checked={!!get(perms, ed.value)} onChange={handlePermissionChange(ed.value)} value={ed.value} />} label={ed.title} />
                ))}
              </FormGroup>
            </FormControl>
          </div>

          <div style={{ marginTop: 16 }}>
            <CreateTitle>{'USER GROUPS'}</CreateTitle>
            <FormControl component="fieldset">
              <FormGroup>
                {userGroups.data.map((ug, i) => (
                  <FormControlLabel key={i} control={<Checkbox checked={!!get(ugs, ug.id)} onChange={handleUgChange(ug.id)} value={ug.id} />} label={ug.name} />
                ))}
              </FormGroup>
            </FormControl>
          </div>
        </CreatePermsContainer>
      </CreateFlex>

      <CreateButtonContainer>
        <CreateButton startIcon={<Icon>cancel</Icon>} color="secondary" variant="contained" onClick={clear} disabled={!canClear}>
          CLEAR
        </CreateButton>

        <CreateButton
          startIcon={sending ? <Loader size={18} /> : <Icon>person_add</Icon>}
          color="secondary"
          variant="contained"
          disabled={!canSubmit}
          submitVariant
          onClick={submit}
        >
          CREATE USERS
        </CreateButton>
      </CreateButtonContainer>

      <Modal {...modal} />
    </CreateContainer>
  );
};
